import { router } from "@inertiajs/react";
import { NAV_PREFETCH_CACHE_FOR } from "@ui/lib/prefetch-config";
import { type PrefetchOptions, PrefetchProvider as UIPrefetchProvider } from "@ui/lib/prefetch-context";
import { type PropsWithChildren, useEffect } from "react";
import { flushSearchSessions } from "@/lib/search-session-store";

function inertiaPrefetch(href: string, options?: PrefetchOptions) {
  // `cacheFor` must always be a defined value: Inertia's prefetch cache runs it
  // through timeToMs, and an explicit `undefined` (vs an omitted key) throws on
  // `undefined.endsWith`. Default to the nav cache window when callers omit it.
  router.prefetch(
    href,
    {},
    {
      cacheFor: options?.cacheFor ?? NAV_PREFETCH_CACHE_FOR,
      cacheTags: options?.cacheTags,
    },
  );
}

function useFlushPrefetchOnMutations() {
  useEffect(() => {
    let pendingMethod: string | null = null;

    const offBefore = router.on("before", (event) => {
      pendingMethod = event.detail.visit.method;
    });

    const offSuccess = router.on("success", () => {
      if (pendingMethod && pendingMethod !== "get") {
        router.flushAll();
        // Mutations may also change what a search returns — drop cached
        // search hits/snapshots (clients and refinements survive).
        flushSearchSessions();
      }
      pendingMethod = null;
    });

    const offFinish = router.on("finish", () => {
      pendingMethod = null;
    });

    return () => {
      offBefore();
      offSuccess();
      offFinish();
    };
  }, []);
}

export function PrefetchProvider({ children }: PropsWithChildren) {
  useFlushPrefetchOnMutations();
  return <UIPrefetchProvider prefetch={inertiaPrefetch}>{children}</UIPrefetchProvider>;
}
